import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class ConnectionOptimizerService extends Service {
  @service('graphql-performance') graphqlPerformance;

  @tracked connectionPools = new Map();
  @tracked connectionMetrics = new Map();
  @tracked healthChecks = new Map();

  // Connection pool configurations
  POOL_CONFIGS = {
    graphql: {
      maxConnections: 20,
      minConnections: 2,
      idleTimeout: 30000, // 30 seconds
      connectionTimeout: 10000, // 10 seconds
      retryAttempts: 3,
      retryDelay: 1000,
    },
    contentful: {
      maxConnections: 10,
      minConnections: 1,
      idleTimeout: 60000, // 1 minute
      connectionTimeout: 15000, // 15 seconds
      retryAttempts: 2,
      retryDelay: 2000,
    },
    external_api: {
      maxConnections: 5,
      minConnections: 1,
      idleTimeout: 45000, // 45 seconds
      connectionTimeout: 8000, // 8 seconds
      retryAttempts: 2,
      retryDelay: 1500,
    },
  };

  // Performance thresholds
  PERFORMANCE_THRESHOLDS = {
    slowQuery: 2000, // 2 seconds
    slowConnection: 5000, // 5 seconds
    highErrorRate: 0.05, // 5% error rate
    lowAvailability: 0.95, // 95% availability
  };

  constructor() {
    super(...arguments);
    this.initializeConnectionPools();
    this.startHealthCheckScheduler();
  }

  /**
   * Initializes connection pools for different services
   */
  initializeConnectionPools() {
    Object.keys(this.POOL_CONFIGS).forEach((service) => {
      this.createConnectionPool(service, this.POOL_CONFIGS[service]);
    });
  }

  /**
   * Creates a connection pool for a specific service
   * @param {string} serviceName - Name of the service
   * @param {Object} config - Pool configuration
   */
  createConnectionPool(serviceName, config) {
    const pool = {
      serviceName,
      config,
      connections: new Map(),
      availableConnections: [],
      activeConnections: new Set(),
      waitingQueue: [],
      created: Date.now(),
      stats: {
        totalConnections: 0,
        activeConnections: 0,
        waitingRequests: 0,
        successfulConnections: 0,
        failedConnections: 0,
        timeouts: 0,
        averageConnectionTime: 0,
        totalConnectionTime: 0,
      },
    };

    this.connectionPools.set(serviceName, pool);

    // Pre-create minimum connections
    this.ensureMinimumConnections(serviceName);

    console.info(`Connection pool created for ${serviceName}`, {
      config,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Gets a connection from the pool
   * @param {string} serviceName - Service name
   * @param {Object} options - Connection options
   * @returns {Promise<Object>} Connection object
   */
  async getConnection(serviceName, options = {}) {
    const pool = this.connectionPools.get(serviceName);
    if (!pool) {
      throw new Error(`No connection pool found for service: ${serviceName}`);
    }

    const startTime = Date.now();
    let connection = null;

    try {
      // Try to get an available connection
      connection = this.getAvailableConnection(pool);

      if (!connection) {
        // Create new connection if under limit
        if (pool.stats.totalConnections < pool.config.maxConnections) {
          connection = await this.createConnection(pool, options);
        } else {
          // Wait for available connection
          connection = await this.waitForConnection(pool, options);
        }
      }

      // Mark connection as active
      pool.activeConnections.add(connection.id);
      pool.stats.activeConnections = pool.activeConnections.size;

      // Update metrics
      const connectionTime = Date.now() - startTime;
      this.updateConnectionMetrics(serviceName, connectionTime, true);

      return connection;
    } catch (error) {
      const connectionTime = Date.now() - startTime;
      this.updateConnectionMetrics(serviceName, connectionTime, false);

      console.error(`Failed to get connection for ${serviceName}:`, error);
      throw error;
    }
  }

  /**
   * Returns a connection to the pool
   * @param {string} serviceName - Service name
   * @param {Object} connection - Connection to return
   */
  releaseConnection(serviceName, connection) {
    const pool = this.connectionPools.get(serviceName);
    if (!pool || !connection) return;

    // Remove from active connections
    pool.activeConnections.delete(connection.id);
    pool.stats.activeConnections = pool.activeConnections.size;

    // Check if connection is still healthy
    if (this.isConnectionHealthy(connection)) {
      // Return to available pool
      pool.availableConnections.push(connection);
      connection.lastUsed = Date.now();

      // Process waiting queue
      this.processWaitingQueue(pool);
    } else {
      // Remove unhealthy connection
      this.removeConnection(pool, connection);
    }
  }

  /**
   * Gets an available connection from the pool
   * @param {Object} pool - Connection pool
   * @returns {Object|null} Available connection or null
   */
  getAvailableConnection(pool) {
    // Remove idle connections first
    this.cleanupIdleConnections(pool);

    return pool.availableConnections.shift() || null;
  }

  /**
   * Creates a new connection for the pool
   * @param {Object} pool - Connection pool
   * @param {Object} options - Connection options
   * @returns {Promise<Object>} New connection
   */
  async createConnection(pool, options) {
    const connectionId = `${pool.serviceName}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const startTime = Date.now();

    try {
      const connection = {
        id: connectionId,
        serviceName: pool.serviceName,
        created: startTime,
        lastUsed: startTime,
        requestCount: 0,
        errorCount: 0,
        isHealthy: true,
        metadata: options.metadata || {},
      };

      // Add connection-specific initialization based on service
      await this.initializeConnection(connection, pool.serviceName, options);

      pool.connections.set(connectionId, connection);
      pool.stats.totalConnections++;
      pool.stats.successfulConnections++;

      const connectionTime = Date.now() - startTime;
      pool.stats.totalConnectionTime += connectionTime;
      pool.stats.averageConnectionTime =
        pool.stats.totalConnectionTime / pool.stats.successfulConnections;

      console.debug(
        `Created connection ${connectionId} for ${pool.serviceName} in ${connectionTime}ms`,
      );

      return connection;
    } catch (error) {
      pool.stats.failedConnections++;
      console.error(
        `Failed to create connection for ${pool.serviceName}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Initializes connection based on service type
   * @param {Object} connection - Connection object
   * @param {string} serviceName - Service name
   * @param {Object} options - Connection options
   */
  async initializeConnection(connection, serviceName, options) {
    switch (serviceName) {
      case 'graphql':
        await this.initializeGraphQLConnection(connection, options);
        break;
      case 'contentful':
        await this.initializeContentfulConnection(connection, options);
        break;
      case 'external_api':
        await this.initializeExternalAPIConnection(connection, options);
        break;
      default:
        // Generic connection initialization
        connection.endpoint = options.endpoint || 'unknown';
    }
  }

  /**
   * Initializes GraphQL connection
   * @param {Object} connection - Connection object
   * @param {Object} options - Connection options
   */
  async initializeGraphQLConnection(connection, options) {
    connection.endpoint =
      options.endpoint ||
      'https://graphql.contentful.com/content/v1/spaces/your-space-id';
    connection.headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.accessToken || 'your-token'}`,
      ...options.headers,
    };

    // Test connection with a simple introspection query
    await this.testConnection(connection, {
      query: '{ __typename }',
      timeout: 5000,
    });
  }

  /**
   * Initializes Contentful connection
   * @param {Object} connection - Connection object
   * @param {Object} options - Connection options
   */
  async initializeContentfulConnection(connection, options) {
    connection.endpoint =
      options.endpoint || 'https://cdn.contentful.com/spaces/your-space-id';
    connection.headers = {
      Authorization: `Bearer ${options.accessToken || 'your-token'}`,
      ...options.headers,
    };

    // Test connection with a simple content type request
    await this.testConnection(connection, {
      url: `${connection.endpoint}/content_types?limit=1`,
      timeout: 10000,
    });
  }

  /**
   * Initializes external API connection
   * @param {Object} connection - Connection object
   * @param {Object} options - Connection options
   */
  async initializeExternalAPIConnection(connection, options) {
    connection.endpoint = options.endpoint || 'https://api.example.com';
    connection.headers = {
      'User-Agent': '1vX.gg/1.0',
      ...options.headers,
    };

    // Test basic connectivity
    await this.testConnection(connection, {
      url: connection.endpoint,
      timeout: 8000,
    });
  }

  /**
   * Tests connection health
   * @param {Object} connection - Connection object
   * @param {Object} testOptions - Test parameters
   */
  async testConnection(connection, testOptions) {
    const timeout = testOptions.timeout || 5000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      let response;

      if (testOptions.query) {
        // GraphQL test
        response = await fetch(connection.endpoint, {
          method: 'POST',
          headers: connection.headers,
          body: JSON.stringify({ query: testOptions.query }),
          signal: controller.signal,
        });
      } else {
        // HTTP test
        response = await fetch(testOptions.url, {
          method: 'HEAD',
          headers: connection.headers,
          signal: controller.signal,
        });
      }

      if (!response.ok) {
        throw new Error(`Connection test failed: ${response.status}`);
      }

      connection.lastHealthCheck = Date.now();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Waits for an available connection
   * @param {Object} pool - Connection pool
   * @param {Object} options - Connection options
   * @returns {Promise<Object>} Available connection
   */
  async waitForConnection(pool, options) {
    return new Promise((resolve, reject) => {
      const timeout = options.timeout || pool.config.connectionTimeout;

      const waitingRequest = {
        resolve,
        reject,
        timestamp: Date.now(),
        timeout: setTimeout(() => {
          // Remove from waiting queue
          const index = pool.waitingQueue.indexOf(waitingRequest);
          if (index !== -1) {
            pool.waitingQueue.splice(index, 1);
          }

          pool.stats.timeouts++;
          reject(
            new Error(
              `Connection timeout for ${pool.serviceName} after ${timeout}ms`,
            ),
          );
        }, timeout),
      };

      pool.waitingQueue.push(waitingRequest);
      pool.stats.waitingRequests = pool.waitingQueue.length;
    });
  }

  /**
   * Processes waiting queue when connections become available
   * @param {Object} pool - Connection pool
   */
  processWaitingQueue(pool) {
    while (
      pool.waitingQueue.length > 0 &&
      pool.availableConnections.length > 0
    ) {
      const waitingRequest = pool.waitingQueue.shift();
      const connection = pool.availableConnections.shift();

      clearTimeout(waitingRequest.timeout);
      pool.stats.waitingRequests = pool.waitingQueue.length;

      waitingRequest.resolve(connection);
    }
  }

  /**
   * Checks if connection is healthy
   * @param {Object} connection - Connection to check
   * @returns {boolean} Whether connection is healthy
   */
  isConnectionHealthy(connection) {
    const now = Date.now();

    // Check if connection has been used recently
    if (now - connection.lastUsed > 300000) {
      // 5 minutes
      return false;
    }

    // Check error rate
    if (connection.requestCount > 0) {
      const errorRate = connection.errorCount / connection.requestCount;
      if (errorRate > this.PERFORMANCE_THRESHOLDS.highErrorRate) {
        return false;
      }
    }

    return connection.isHealthy;
  }

  /**
   * Removes unhealthy connections
   * @param {Object} pool - Connection pool
   */
  cleanupIdleConnections(pool) {
    const now = Date.now();

    pool.availableConnections = pool.availableConnections.filter(
      (connection) => {
        const isIdle = now - connection.lastUsed > pool.config.idleTimeout;

        if (isIdle || !this.isConnectionHealthy(connection)) {
          this.removeConnection(pool, connection);
          return false;
        }

        return true;
      },
    );
  }

  /**
   * Removes a connection from the pool
   * @param {Object} pool - Connection pool
   * @param {Object} connection - Connection to remove
   */
  removeConnection(pool, connection) {
    pool.connections.delete(connection.id);
    pool.activeConnections.delete(connection.id);
    pool.stats.totalConnections--;
    pool.stats.activeConnections = pool.activeConnections.size;

    console.debug(
      `Removed connection ${connection.id} from ${pool.serviceName}`,
    );

    // Ensure minimum connections
    this.ensureMinimumConnections(pool.serviceName);
  }

  /**
   * Ensures minimum number of connections in pool
   * @param {string} serviceName - Service name
   */
  async ensureMinimumConnections(serviceName) {
    const pool = this.connectionPools.get(serviceName);
    if (!pool) return;

    const needed = pool.config.minConnections - pool.stats.totalConnections;

    for (let i = 0; i < needed; i++) {
      try {
        const connection = await this.createConnection(pool, {});
        pool.availableConnections.push(connection);
      } catch (error) {
        console.warn(
          `Failed to create minimum connection for ${serviceName}:`,
          error,
        );
        break; // Stop trying if we can't create connections
      }
    }
  }

  /**
   * Updates connection metrics
   * @param {string} serviceName - Service name
   * @param {number} responseTime - Response time in ms
   * @param {boolean} success - Whether operation was successful
   */
  updateConnectionMetrics(serviceName, responseTime, success) {
    let metrics = this.connectionMetrics.get(serviceName);

    if (!metrics) {
      metrics = {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        totalResponseTime: 0,
        averageResponseTime: 0,
        minResponseTime: Infinity,
        maxResponseTime: 0,
        slowRequests: 0,
        lastRequest: Date.now(),
      };
      this.connectionMetrics.set(serviceName, metrics);
    }

    metrics.totalRequests++;
    metrics.totalResponseTime += responseTime;
    metrics.averageResponseTime =
      metrics.totalResponseTime / metrics.totalRequests;
    metrics.minResponseTime = Math.min(metrics.minResponseTime, responseTime);
    metrics.maxResponseTime = Math.max(metrics.maxResponseTime, responseTime);
    metrics.lastRequest = Date.now();

    if (success) {
      metrics.successfulRequests++;
    } else {
      metrics.failedRequests++;
    }

    if (responseTime > this.PERFORMANCE_THRESHOLDS.slowQuery) {
      metrics.slowRequests++;
    }
  }

  /**
   * Starts health check scheduler
   */
  startHealthCheckScheduler() {
    // Run health checks every 5 minutes
    setInterval(
      () => {
        this.runHealthChecks();
      },
      5 * 60 * 1000,
    );

    // Run cleanup every minute
    setInterval(() => {
      this.cleanupConnections();
    }, 60 * 1000);
  }

  /**
   * Runs health checks on all pools
   */
  async runHealthChecks() {
    for (const [serviceName, pool] of this.connectionPools.entries()) {
      try {
        const healthStatus = await this.checkPoolHealth(pool);
        this.healthChecks.set(serviceName, {
          ...healthStatus,
          timestamp: new Date().toISOString(),
        });

        console.debug(
          `Health check completed for ${serviceName}:`,
          healthStatus,
        );
      } catch (error) {
        console.error(`Health check failed for ${serviceName}:`, error);
        this.healthChecks.set(serviceName, {
          healthy: false,
          error: error.message,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }

  /**
   * Checks health of a connection pool
   * @param {Object} pool - Connection pool
   * @returns {Promise<Object>} Health status
   */
  async checkPoolHealth(pool) {
    const metrics = this.connectionMetrics.get(pool.serviceName) || {};
    const errorRate =
      metrics.totalRequests > 0
        ? metrics.failedRequests / metrics.totalRequests
        : 0;

    const availability =
      metrics.totalRequests > 0
        ? metrics.successfulRequests / metrics.totalRequests
        : 1;

    const isHealthy =
      errorRate < this.PERFORMANCE_THRESHOLDS.highErrorRate &&
      availability >= this.PERFORMANCE_THRESHOLDS.lowAvailability;

    return {
      healthy: isHealthy,
      availability: availability,
      errorRate: errorRate,
      averageResponseTime: metrics.averageResponseTime || 0,
      totalConnections: pool.stats.totalConnections,
      activeConnections: pool.stats.activeConnections,
      waitingRequests: pool.stats.waitingRequests,
      lastActivity: metrics.lastRequest,
    };
  }

  /**
   * Cleans up old connections and resets pools if needed
   */
  cleanupConnections() {
    for (const [serviceName, pool] of this.connectionPools.entries()) {
      this.cleanupIdleConnections(pool);

      // Reset metrics if no recent activity
      const metrics = this.connectionMetrics.get(serviceName);
      if (metrics && Date.now() - metrics.lastRequest > 24 * 60 * 60 * 1000) {
        // 24 hours
        this.connectionMetrics.delete(serviceName);
      }
    }
  }

  /**
   * Gets comprehensive connection statistics
   * @returns {Object} Connection statistics
   */
  getConnectionStats() {
    const stats = {
      pools: {},
      overall: {
        totalPools: this.connectionPools.size,
        totalConnections: 0,
        totalActiveConnections: 0,
        totalWaitingRequests: 0,
        healthyPools: 0,
      },
    };

    for (const [serviceName, pool] of this.connectionPools.entries()) {
      const metrics = this.connectionMetrics.get(serviceName) || {};
      const health = this.healthChecks.get(serviceName) || {};

      stats.pools[serviceName] = {
        ...pool.stats,
        metrics: metrics,
        health: health,
        config: pool.config,
      };

      stats.overall.totalConnections += pool.stats.totalConnections;
      stats.overall.totalActiveConnections += pool.stats.activeConnections;
      stats.overall.totalWaitingRequests += pool.stats.waitingRequests;

      if (health.healthy !== false) {
        stats.overall.healthyPools++;
      }
    }

    return stats;
  }

  /**
   * Optimizes connection pools based on usage patterns
   */
  optimizePools() {
    for (const [serviceName, pool] of this.connectionPools.entries()) {
      const metrics = this.connectionMetrics.get(serviceName);
      if (!metrics || metrics.totalRequests < 100) continue; // Need enough data

      // Adjust pool size based on usage
      const avgActiveConnections = pool.stats.activeConnections;
      const maxConnections = pool.config.maxConnections;

      if (avgActiveConnections > maxConnections * 0.8) {
        // Pool is often near capacity, consider increasing
        console.info(`Consider increasing max connections for ${serviceName}`, {
          current: maxConnections,
          suggested: Math.min(maxConnections * 1.5, 50),
          utilization: (avgActiveConnections / maxConnections) * 100,
        });
      }

      if (metrics.slowRequests > metrics.totalRequests * 0.1) {
        // High percentage of slow requests
        console.warn(`High slow request rate for ${serviceName}`, {
          slowRequests: metrics.slowRequests,
          totalRequests: metrics.totalRequests,
          percentage: (metrics.slowRequests / metrics.totalRequests) * 100,
        });
      }
    }
  }
}
